import axios from "axios";

/** 
 * ===== Configuração da API Highlightly Sports =====
 * API externa para dados reais de partidas de futebol
 * USANDO PLANO PRO DIRETO (não RapidAPI)
 */

const HIGHLIGHTLY_BASE_URL = "https://sports.highlightly.net";

// IMPORTANTE: Usando chave da API PRO do Highlightly
// REACT_APP_RAPIDAPI_KEY contém a chave do plano PRO
const HIGHLIGHTLY_API_KEY = process.env.REACT_APP_RAPIDAPI_KEY || "";

if (!HIGHLIGHTLY_API_KEY) {
  console.warn("[Highlightly] Chave da API não configurada. Adicione REACT_APP_RAPIDAPI_KEY ao arquivo .env");
}

const highlightlyAPI = axios.create({
  baseURL: HIGHLIGHTLY_BASE_URL,
  timeout: 15000,
  headers: {
    'x-rapidapi-key': HIGHLIGHTLY_API_KEY
    // NÃO incluir 'x-rapidapi-host' porque estamos usando a API direta, não RapidAPI
  }
});

// Interceptor de erros
highlightlyAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url;
    const msg = error.response?.data?.message || error.message;
    console.error(`[Highlightly API] Erro ${status} em ${url}: ${msg}`);
    return Promise.reject(error);
  }
);

/** ===== Helper para remover parâmetros vazios ===== */
const cleanParams = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

/** ======================================
 * ENDPOINTS DE PARTIDAS DE FUTEBOL
 * ====================================== */

/**
 * Buscar partidas por filtros
 * @param {Object} params
 * @param {string} params.date - Data no formato YYYY-MM-DD
 * @param {string} params.dateFrom - Data inicial (YYYY-MM-DD)
 * @param {string} params.dateTo - Data final (YYYY-MM-DD)
 * @param {string} params.leagueName - Nome da liga (ex: "Premier League", "La Liga")
 * @param {number} params.leagueId - ID da liga
 * @param {string} params.countryCode - Código do país (ISO 3166)
 * @param {string} params.teamName - Nome do time
 * @param {number} params.teamId - ID do time
 * @param {string} params.timezone - Timezone (ex: "America/Sao_Paulo")
 * @param {number} params.limit - Limite de resultados (padrão: 50)
 * @param {number} params.offset - Offset para paginação
 */
export const getMatches = (params = {}) => {
  return highlightlyAPI.get("/football/matches", {
    params: cleanParams({
      ...params,
      timezone: params.timezone || "America/Sao_Paulo"
    })
  });
};

/**
 * Buscar partidas de 2025
 * @param {Object} params
 * @param {string} params.leagueName - Nome da liga (opcional)
 * @param {number} params.leagueId - ID da liga (opcional)
 * @param {number} params.limit - Limite de resultados
 */
export const getMatches2025 = (params = {}) => {
  return getMatches({
    dateFrom: "2025-01-01",
    dateTo: "2025-12-31",
    ...params
  });
};

/**
 * Buscar detalhes completos de uma partida
 * @param {string|number} matchId - ID da partida
 */
export const getMatchDetails = (matchId) => {
  return highlightlyAPI.get(`/football/matches/${matchId}`);
};

/**
 * Buscar escalações de uma partida
 * @param {string|number} matchId - ID da partida
 */
export const getMatchLineups = (matchId) => {
  return highlightlyAPI.get(`/football/lineups/${matchId}`);
};

/**
 * Buscar estatísticas de uma partida
 * @param {string|number} matchId - ID da partida
 */
export const getMatchStats = (matchId) => {
  return highlightlyAPI.get(`/football/statistics/${matchId}`);
};

/**
 * Buscar eventos de uma partida (gols, cartões, substituições)
 * @param {string|number} matchId - ID da partida
 */
export const getMatchEvents = (matchId) => {
  return highlightlyAPI.get(`/football/events/${matchId}`);
};

/**
 * Buscar destaques/vídeos de uma partida
 * @param {Object} params
 * @param {number} params.matchId - ID da partida
 * @param {string} params.type - Tipo de destaque ("VERIFIED" ou "UNVERIFIED")
 * @param {number} params.limit - Limite de resultados
 */
export const getMatchHighlights = (params = {}) => {
  return highlightlyAPI.get("/football/highlights", {
    params: cleanParams(params)
  });
};

/** ======================================
 * ENDPOINTS DE LIGAS
 * ====================================== */

/**
 * Buscar todas as ligas
 * @param {Object} params
 * @param {string} params.leagueName - Nome da liga
 * @param {string} params.countryCode - Código do país
 * @param {string} params.countryName - Nome do país
 * @param {number} params.limit - Limite de resultados
 * @param {number} params.offset - Offset para paginação
 */
export const getLeagues = (params = {}) => {
  return highlightlyAPI.get("/football/leagues", {
    params: cleanParams(params)
  });
};

/**
 * Buscar detalhes de uma liga
 * @param {string|number} leagueId - ID da liga
 */
export const getLeagueDetails = (leagueId) => {
  return highlightlyAPI.get(`/football/leagues/${leagueId}`);
};

/**
 * Buscar classificação de uma liga
 * @param {Object} params
 * @param {number} params.leagueId - ID da liga (obrigatório)
 * @param {string} params.season - Temporada (ex: "2024/2025")
 * @param {string} params.timezone - Timezone
 */
export const getStandings = (params = {}) => {
  return highlightlyAPI.get("/football/standings", {
    params: cleanParams({
      ...params,
      timezone: params.timezone || "America/Sao_Paulo"
    })
  });
};

/** ======================================
 * ENDPOINTS DE TIMES
 * ====================================== */

/**
 * Buscar times
 * @param {Object} params
 * @param {string} params.name - Nome do time
 * @param {string} params.countryCode - Código do país
 * @param {number} params.limit - Limite de resultados
 * @param {number} params.offset - Offset para paginação
 */
export const getTeams = (params = {}) => {
  return highlightlyAPI.get("/football/teams", {
    params: cleanParams(params)
  });
};

/**
 * Buscar detalhes de um time
 * @param {string|number} teamId - ID do time
 */
export const getTeamDetails = (teamId) => {
  return highlightlyAPI.get(`/football/teams/${teamId}`);
};

/**
 * Buscar estatísticas de um time
 * @param {string|number} teamId - ID do time
 * @param {string} fromDate - Data inicial (YYYY-MM-DD) - obrigatório
 * @param {string} timezone - Timezone
 */
export const getTeamStatistics = (teamId, fromDate, timezone = "America/Sao_Paulo") => {
  return highlightlyAPI.get(`/football/teams/statistics/${teamId}`, {
    params: {
      fromDate,
      timezone
    }
  });
};

/** ======================================
 * ENDPOINTS DE PAÍSES
 * ====================================== */

/**
 * Buscar todos os países
 * @param {string} name - Nome do país (opcional)
 */
export const getCountries = (name = null) => {
  return highlightlyAPI.get("/football/countries", {
    params: cleanParams({ name })
  });
};

/**
 * Buscar país por código
 * @param {string} countryCode - Código do país (ISO 3166)
 */
export const getCountryByCode = (countryCode) => {
  return highlightlyAPI.get(`/football/countries/${countryCode}`);
};

/** ======================================
 * UTILITÁRIOS
 * ====================================== */

/**
 * Formatar dados da partida para o formato usado no frontend
 */
export const formatMatchData = (apiMatch) => {
  if (!apiMatch) return null;

  return {
    id: apiMatch.id,
    homeTeam: {
      id: apiMatch.homeTeam?.id,
      name: apiMatch.homeTeam?.name,
      logo: apiMatch.homeTeam?.logo,
      score: apiMatch.homeTeam?.score || 0
    },
    awayTeam: {
      id: apiMatch.awayTeam?.id,
      name: apiMatch.awayTeam?.name,
      logo: apiMatch.awayTeam?.logo,
      score: apiMatch.awayTeam?.score || 0
    },
    status: apiMatch.status?.name || apiMatch.status,
    isLive: apiMatch.status?.ongoing || false,
    competition: apiMatch.league?.name,
    date: apiMatch.dateTime || apiMatch.date,
    venue: apiMatch.venue?.name || 'N/A',
    referee: apiMatch.referee?.name || 'N/A',
    season: apiMatch.league?.season
  };
};

console.log("[Highlightly API] Configurado com baseURL:", HIGHLIGHTLY_BASE_URL);
